import React from 'react';
import { Menu, ChevronLeft, HelpCircle, X } from 'lucide-react';
import { MenuButton, Button } from '../design-system';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

/**
 * DynamicHeader - A reusable header component that adapts based on the current section
 * 
 * Features:
 * - Dashboard: Shows menu icon + app name
 * - Other sections: Shows back button + section name
 * - Help icon on the right side for additional information
 * - Fully customizable section names and app name
 * - Consistent styling across all dashboards
 * - Demo mode exit button for therapist viewing patient portal
 * 
 * @param {string} activeSection - Current active section (e.g., 'dashboard', 'appointments')
 * @param {function} onMenuClick - Callback when menu button is clicked
 * @param {function} onBackClick - Callback when back button is clicked
 * @param {function} onHelpClick - Callback when help icon is clicked
 * @param {object} sectionNames - Mapping of section IDs to display names
 * @param {boolean} showMenuOnDashboard - Whether to show menu button on dashboard (default: true)
 * @param {string} appName - Application name to display (default: "TheraEngage")
 * @param {boolean} showHelpIcon - Whether to show help icon (default: true)
 * @param {boolean} showDemoExitButton - Whether to show exit demo mode button
 * @param {function} onExitDemo - Callback when exit demo button is clicked
 * 
 * @example
 * // Basic usage in PatientDashboard
 * <DynamicHeader
 *   activeSection={activeSection}
 *   onMenuClick={() => setSidebarOpen(true)}
 *   onBackClick={() => setActiveSection('dashboard')}
 *   onHelpClick={() => setShowHelp(true)}
 *   sectionNames={{
 *     appointments: 'Appointments',
 *     tasks: 'My Tasks',
 *     chat: 'AI Chat'
 *   }}
 *   appName="TheraEngage"
 *   showDemoExitButton={isDemoMode}
 *   onExitDemo={() => {
 *     toggleDemoMode(false);
 *     navigate('/dashboard/therapist');
 *   }}
 * />
 */
const DynamicHeader = ({ 
  activeSection, 
  onMenuClick, 
  onBackClick, 
  onHelpClick,
  sectionNames = {},
  showMenuOnDashboard = true,
  appName = "TheraEngage",
  showHelpIcon = true
}) => {
  const navigate = useNavigate();
  const { toggleDemoMode } = useAuth();
  
  // Check if demo mode is enabled (from sessionStorage)
  const showDemoExitButton = (() => {
    try {
      return sessionStorage.getItem('theraengage_demo_mode') === 'true';
    } catch {
      return false;
    }
  })();
  
  const handleExitDemo = () => {
    toggleDemoMode(false);
    navigate('/dashboard/therapist');
  };
  
  const isDashboard = activeSection === 'dashboard';
  
  return (
    <div className="flex-shrink-0 bg-white shadow-sm border-b border-gray-100 px-4 md:px-6 py-3 md:py-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          {isDashboard ? (
            // Dashboard: Show menu icon + App name
            <>
              {showMenuOnDashboard && (
                <MenuButton
                  onClick={onMenuClick}
                  variant="primary"
                  size="medium"
                >
                  <Menu className="h-5 w-5" />
                </MenuButton>
              )}
              <div>
                <h1 className="text-lg md:text-2xl font-bold" style={{ color: 'var(--color-primary)' }}>
                  {appName}
                </h1>
              </div>
            </>
          ) : (
            // Other sections: Show back button + section name
            <>
              <MenuButton
                onClick={onBackClick}
                variant="primary"
                size="medium"
              >
                <ChevronLeft className="h-5 w-5" />
              </MenuButton>
              <div>
                <h1 className="text-lg md:text-2xl font-bold" style={{ color: 'var(--color-primary)' }}>
                  {sectionNames[activeSection] || activeSection}
                </h1>
              </div>
            </>
          )}
        </div>
        <div className="flex items-center space-x-2 md:space-x-4">
          {/* Demo Mode Exit Button - Show when in demo mode */}
          {showDemoExitButton && (
            <Button
              onClick={handleExitDemo}
              variant="secondary"
              size="small"
              title="Exit Demo Mode"
            >
              <X className="h-4 w-4 mr-1" />
              Exit Demo
            </Button>
          )}
          {/* Help Icon - Only show when not on dashboard and help icon is enabled */}
          {!isDashboard && showHelpIcon && onHelpClick && (
            <MenuButton
              onClick={onHelpClick}
              variant="primary"
              size="medium"
              title="Help & Information"
            >
              <HelpCircle className="h-5 w-5" />
            </MenuButton>
          )}
        </div>
      </div>
    </div>
  );
};

export default DynamicHeader;
